import { useState, useCallback } from 'react';
import { Modal, message } from 'antd';
import { pwh201Api } from '../../../services/pwh201Api';
import type { FH110 } from '../../../services/pwh201Api';

interface UsePWH201ApprovalOptions {
  record: FH110 | null;
  isEditing: boolean;
  loadRecordWithNames: (id: string) => Promise<void>;
}

export function usePWH201Approval({ record, isEditing, loadRecordWithNames }: UsePWH201ApprovalOptions) {
  const [approvalHistoryVisible, setApprovalHistoryVisible] = useState(false);

  const handleSubmit = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要將請款單 ${record.fh110_01} 送審？`,
      okText: '送審',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await pwh201Api.submitForApproval(record.fh110_01);
          message.success(resp.message || '送審成功');
          await loadRecordWithNames(record.fh110_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '送審失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleApprove = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准請款單 ${record.fh110_01}？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await pwh201Api.approve(record.fh110_01);
          message.success(resp.message || '核准成功');
          await loadRecordWithNames(record.fh110_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleReject = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回請款單 ${record.fh110_01}？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await pwh201Api.reject(record.fh110_01);
          message.success(resp.message || '退回成功');
          await loadRecordWithNames(record.fh110_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '退回失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleWriteoff = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${record.fh110_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await pwh201Api.writeoff(record.fh110_01);
          message.success(resp.message || '沖銷成功');
          await loadRecordWithNames(record.fh110_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '沖銷失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleViewHistory = useCallback(() => {
    if (!record) return;
    setApprovalHistoryVisible(true);
  }, [record]);

  return {
    approvalHistoryVisible,
    setApprovalHistoryVisible,
    handleSubmit,
    handleApprove,
    handleReject,
    handleWriteoff,
    handleViewHistory,
  };
}
